import type { FormikValues } from 'formik';
import { Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import RoundedButton from '@components/RoundedButton';

import Dialog from './Dialog';
import type { FormDialogProps } from './types';

const messages = defineMessages({
  submit: {
    id: 'GLOBAL.submit',
    defaultMessage: 'Submit',
  },
});

/**
 * Dialog component whose content is wrapped in Formik
 */
function FormDialog<TValues extends FormikValues>(
  props: FormDialogProps<TValues>
) {
  const {
    initialValues,
    validationSchema,
    content,
    disableSubmit,
    loading = false,
    onSubmit,
    onClose,
    persistent,
  } = props;

  const { formatMessage } = useIntl();

  const handleSubmit: FormDialogProps<TValues>['onSubmit'] = async (
    values,
    helpers
  ) => {
    await onSubmit?.(values, helpers);
    helpers.resetForm();
    onClose();
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, resetForm, values }) => {
        const handleClose = () => {
          resetForm();
          onClose();
        };

        const disableSubmitBtn = disableSubmit?.(values) ?? false;

        return (
          <Dialog
            {...props}
            onClose={handleClose}
            persistent={persistent}
            Wrapper={Form}
            content={<ContentLoader loading={loading}>{content}</ContentLoader>}
            actions={
              <RoundedButton
                disabled={disableSubmitBtn || isSubmitting}
                type="submit"
              >
                {formatMessage(messages.submit)}
              </RoundedButton>
            }
          />
        );
      }}
    </Formik>
  );
}

export default FormDialog;
